import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, AlertTriangle, ClipboardList, Boxes, ClipboardCheck,
  History, Download, Undo2, Pencil, ShieldAlert, Check, X, Clock,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import WeeklyOrderTab from "./WeeklyOrderTab";

const UNIT_PRESETS = ["lb", "oz", "kg", "g", "L", "ml", "bag", "case", "each", "pack", "box"];
const emptyItem = { name: "", unit: "lb", quantity: 0, reorder_level: 0, supplier: "", category: "", notes: "" };

export default function Inventory() {
  const { user } = useAuth();
  const { sid } = useParams();
  const [tab, setTab] = useState("weekly");

  return (
    <div className="p-8 lg:p-10">
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">Kitchen</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-inventory-title">Inventory</h1>
        </div>
        <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1 flex-wrap" role="tablist">
          <TabBtn value="weekly" tab={tab} setTab={setTab} icon={<ClipboardList className="w-4 h-4"/>}>Weekly Order</TabBtn>
          <TabBtn value="stock" tab={tab} setTab={setTab} icon={<Boxes className="w-4 h-4"/>}>Current Stock</TabBtn>
          <TabBtn value="count" tab={tab} setTab={setTab} icon={<ClipboardCheck className="w-4 h-4"/>}>Daily Count</TabBtn>
          <TabBtn value="history" tab={tab} setTab={setTab} icon={<History className="w-4 h-4"/>}>Submitted Counts</TabBtn>
          {["admin","super_admin"].includes(user?.role) && <TabBtn value="requests" tab={tab} setTab={setTab} icon={<ShieldAlert className="w-4 h-4"/>}>Late Requests</TabBtn>}
        </div>
      </div>

      {tab === "weekly" && <WeeklyOrderTab storeIdOverride={sid}/>}
      {tab === "stock" && <CurrentStockTab user={user} storeIdOverride={sid}/>}
      {tab === "count" && <DailyCountTab user={user} storeIdOverride={sid}/>}
      {tab === "history" && <SubmittedCountsTab user={user} storeIdOverride={sid}/>}
      {tab === "requests" && ["admin","super_admin"].includes(user?.role) && <LateRequestsTab/>}
    </div>
  );
}

function TabBtn({ value, tab, setTab, icon, children }) {
  return (
    <button
      role="tab"
      aria-selected={tab === value}
      onClick={() => setTab(value)}
      className={`px-4 h-9 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${tab === value ? "bg-neutral-900 text-white" : "text-neutral-600 hover:text-neutral-900"}`}
      data-testid={`inv-tab-${value}`}
    >{icon}{children}</button>
  );
}

/** Renders the correct cutoff banner. Kept flat (no nested ternary) for readability. */
function CutoffBadge({ status }) {
  if (!status?.cutoff_passed) {
    return (
      <span className="text-emerald-600 text-xs font-medium inline-flex items-center gap-1">
        <Check className="w-3 h-3"/>Open for count
      </span>
    );
  }
  if (status?.late_request_approved) {
    return (
      <span className="text-emerald-600 text-xs font-medium inline-flex items-center gap-1">
        <Check className="w-3 h-3"/>Late submission approved
      </span>
    );
  }
  return (
    <span className="text-red-600 text-xs font-medium inline-flex items-center gap-1">
      <AlertTriangle className="w-3 h-3"/>Past cutoff — needs admin approval
    </span>
  );
}

// -------------------- Current Stock --------------------
function CurrentStockTab({ user, storeIdOverride }) {
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeFilter, setStoreFilter] = useState(storeIdOverride || (user?.role === "manager" ? user?.store_id : ""));
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyItem, store_id: storeIdOverride || (user?.role === "manager" ? user?.store_id : "") });

  useEffect(() => { if (storeIdOverride) setStoreFilter(storeIdOverride); }, [storeIdOverride]);

  const load = () => {
    const url = storeFilter ? `/inventory?store_id=${storeFilter}` : "/inventory";
    api.get(url).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => {
    if (["admin","super_admin"].includes(user?.role)) api.get("/stores").then((r) => setStores(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeFilter]);

  const save = async () => {
    try {
      const body = {
        ...form,
        quantity: parseFloat(form.quantity) || 0,
        reorder_level: parseFloat(form.reorder_level) || 0,
      };
      if (editing) await api.put(`/inventory/${editing.id}`, body);
      else await api.post("/inventory", body);
      toast.success(editing ? "Item updated" : "Item added");
      setOpen(false); setEditing(null); setForm({ ...emptyItem, store_id: user?.role === "manager" ? user?.store_id : "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (it) => { setEditing(it); setForm({ ...emptyItem, ...it }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm({ ...emptyItem, store_id: user?.role === "manager" ? user?.store_id : (storeFilter || "") }); setOpen(true); };

  const updateQty = async (it, qty) => {
    try {
      await api.put(`/inventory/${it.id}`, { ...it, quantity: parseFloat(qty) || 0 });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item from stock?")) return;
    try { await api.delete(`/inventory/${id}`); load(); } catch { toast.error("Failed"); }
  };

  const canManage = ["admin","super_admin"].includes(user?.role) || user?.role === "manager";

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        {["admin","super_admin"].includes(user?.role) && (
          <select
            value={storeFilter}
            onChange={(e) => setStoreFilter(e.target.value)}
            className="h-10 rounded-lg border border-neutral-200 bg-white text-sm px-3"
            data-testid="stock-store-filter"
          >
            <option value="">All stores</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="rounded-xl bg-[hsl(var(--primary))]" data-testid="add-inventory"><Plus className="w-4 h-4 mr-1"/>Add Item</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Item" : "New Inventory Item"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="stock-form-name"/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Unit</Label>
                    <div className="flex gap-1">
                      <select value={UNIT_PRESETS.includes(form.unit) ? form.unit : "__custom__"} onChange={(e) => setForm({...form, unit: e.target.value === "__custom__" ? "" : e.target.value})} className="h-10 rounded-lg border border-neutral-200 bg-white text-sm px-2" data-testid="stock-form-unit-select">
                        {UNIT_PRESETS.map((u) => <option key={u} value={u}>{u}</option>)}
                        <option value="__custom__">Custom…</option>
                      </select>
                      <Input value={form.unit} onChange={(e) => setForm({...form, unit: e.target.value})} placeholder="lb, kg, L, bag…" data-testid="stock-form-unit"/>
                    </div>
                  </div>
                  <div><Label>Quantity</Label><Input type="number" step="0.01" value={form.quantity} onChange={(e) => setForm({...form, quantity: e.target.value})} data-testid="stock-form-qty"/></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Reorder level</Label><Input type="number" step="0.01" value={form.reorder_level} onChange={(e) => setForm({...form, reorder_level: e.target.value})}/></div>
                  <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} placeholder="Proteins, Produce, Dry…"/></div>
                </div>
                {["admin","super_admin"].includes(user?.role) && (
                  <div>
                    <Label>Store</Label>
                    <select value={form.store_id || ""} onChange={(e) => setForm({...form, store_id: e.target.value})} className="h-10 w-full rounded-lg border border-neutral-200 bg-white text-sm px-3" data-testid="stock-form-store">
                      <option value="">All stores (shared)</option>
                      {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} rows={2} placeholder="Storage instructions, allergens, suppliers to prefer…"/></div>
                <Button onClick={save} className="w-full rounded-xl bg-[hsl(var(--primary))]" data-testid="stock-form-save">{editing ? "Update" : "Create"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="p-4">Item</th><th className="p-4">Category</th><th className="p-4">Quantity</th><th className="p-4">Reorder</th><th className="p-4">Status</th><th className="p-4"></th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-neutral-500" data-testid="stock-empty">No inventory items yet.</td></tr>
            )}
            {items.map((it) => {
              const low = it.quantity <= it.reorder_level;
              return (
                <tr key={it.id} data-testid={`inventory-row-${it.id}`}>
                  <td className="p-4">
                    <div className="font-medium">{it.name}</div>
                    {it.notes && <div className="text-[11px] text-neutral-500 mt-0.5 max-w-xs truncate">{it.notes}</div>}
                  </td>
                  <td className="p-4 text-neutral-500">{it.category || "—"}</td>
                  <td className="p-4">
                    <Input type="number" step="0.01" defaultValue={it.quantity} onBlur={(e) => updateQty(it, e.target.value)} className="w-24 h-9"/>
                    <span className="text-xs text-neutral-500 ml-2">{it.unit}</span>
                  </td>
                  <td className="p-4">{it.reorder_level} {it.unit}</td>
                  <td className="p-4">
                    {low
                      ? <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium"><AlertTriangle className="w-3 h-3"/>Low stock</span>
                      : <span className="text-emerald-600 text-xs font-medium">In stock</span>}
                  </td>
                  <td className="p-4 whitespace-nowrap">
                    {canManage && (
                      <>
                        <button onClick={() => openEdit(it)} className="text-neutral-500 hover:text-neutral-900 mr-3"><Pencil className="w-4 h-4"/></button>
                        {(["admin","super_admin"].includes(user?.role) || user?.role === "manager") && (
                          <button onClick={() => remove(it.id)} className="text-neutral-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// -------------------- Daily Count --------------------
function DailyCountTab({ user, storeIdOverride }) {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(storeIdOverride || user?.store_id || "");
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);       // stock items to count against
  const [countMap, setCountMap] = useState({}); // id -> {count, notes}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (storeIdOverride) setStoreId(storeIdOverride); }, [storeIdOverride]);

  useEffect(() => {
    if (["admin","super_admin"].includes(user?.role)) api.get("/stores").then((r) => setStores(r.data));
  }, [user]);

  const loadStatus = async () => {
    if (!storeId) return;
    try {
      const [{ data: st }, { data: inv }] = await Promise.all([
        api.get(`/inventory/count-status?store_id=${storeId}`),
        api.get(`/inventory?store_id=${storeId}`),
      ]);
      setStatus(st);
      setItems(inv);
      const map = {};
      inv.forEach((it) => { map[it.id] = { count: 0, notes: "" }; });
      setCountMap(map);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadStatus(); }, [storeId]);

  const canRequestLate = status?.cutoff_passed && !status?.late_request_approved && !["admin","super_admin"].includes(user?.role);
  const canSubmit = !!storeId && (
    !status?.cutoff_passed || status?.late_request_approved || ["admin","super_admin"].includes(user?.role)
  );

  const requestLate = async () => {
    if (!storeId) return;
    setBusy(true);
    try {
      await api.post("/inventory/late-requests", { store_id: storeId, date: status.date, reason });
      toast.success("Late submission request sent to admin");
      setReason("");
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!storeId) return;
    const payload = {
      store_id: storeId,
      items: items.map((it) => ({
        item_id: it.id,
        name: it.name,
        unit: it.unit,
        count: parseFloat(countMap[it.id]?.count) || 0,
        notes: countMap[it.id]?.notes || "",
      })),
    };
    if (payload.items.length === 0) { toast.error("Add stock items first (Current Stock tab)"); return; }
    setBusy(true);
    try {
      await api.post("/inventory/counts", payload);
      toast.success("Inventory count submitted");
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!storeId && ["admin","super_admin"].includes(user?.role)) {
    return (
      <div className="bg-white rounded-2xl border p-6">
        <Label>Pick a store to count for</Label>
        <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="h-10 mt-2 rounded-lg border border-neutral-200 bg-white text-sm px-3 min-w-[240px]" data-testid="count-store-picker">
          <option value="">— select store —</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
    );
  }
  if (!storeId) return <div className="bg-white rounded-2xl border p-6 text-neutral-500 text-sm">Your account is not assigned to a store yet. Ask an admin to assign you.</div>;

  return (
    <div>
      {/* Status banner */}
      <div className="mb-4 rounded-2xl border border-neutral-200/70 bg-white p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-neutral-500"/>
          <span className="font-medium">Cutoff: 12:00 PM</span>
          <span className="text-neutral-500">({status?.timezone || "store local"})</span>
        </div>
        <div className="text-sm text-neutral-500">Today: {status?.date}</div>
        <CutoffBadge status={status}/>
        {status?.already_submitted && <span className="ml-auto text-xs text-neutral-500 italic">You already submitted a count today. Submitting again creates another entry.</span>}
      </div>

      {canRequestLate && (
        <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-orange-600 mt-0.5"/>
            <div className="flex-1">
              <p className="text-sm font-semibold text-orange-900">The 12:00 PM cutoff has passed.</p>
              <p className="text-xs text-orange-800 mt-0.5">Enter a reason and request admin approval to submit a late count.</p>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="mt-2 bg-white" placeholder="Reason for late submission…" data-testid="late-reason"/>
              <Button onClick={requestLate} disabled={busy} className="mt-2 rounded-xl bg-orange-600 hover:bg-orange-700" data-testid="request-late"><ShieldAlert className="w-4 h-4 mr-1"/>Request approval</Button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="p-4">Item</th><th className="p-4">Unit</th><th className="p-4">On-hand count</th><th className="p-4">Notes</th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {items.length === 0 && (
              <tr><td colSpan={4} className="p-10 text-center text-neutral-500" data-testid="count-empty">No stock items to count. Add items in the Current Stock tab first.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id}>
                <td className="p-3">
                  <div className="font-medium">{it.name}</div>
                  {it.category && <div className="text-[11px] text-neutral-400">{it.category}</div>}
                </td>
                <td className="p-3 text-neutral-500">{it.unit}</td>
                <td className="p-3">
                  <Input type="number" step="0.01"
                    value={countMap[it.id]?.count ?? 0}
                    onChange={(e) => setCountMap({...countMap, [it.id]: { ...(countMap[it.id]||{}), count: e.target.value }})}
                    className="w-28 h-9"
                    data-testid={`count-input-${it.id}`}/>
                </td>
                <td className="p-3">
                  <Input value={countMap[it.id]?.notes || ""}
                    onChange={(e) => setCountMap({...countMap, [it.id]: { ...(countMap[it.id]||{}), notes: e.target.value }})}
                    placeholder="Damaged, in walk-in, expiring soon…"
                    className="h-9"
                    data-testid={`count-notes-${it.id}`}/>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {items.length > 0 && (
        <div className="mt-4 flex justify-end">
          <Button onClick={submit} disabled={!canSubmit || busy} className="rounded-xl bg-[hsl(var(--primary))]" data-testid="submit-count">
            <ClipboardCheck className="w-4 h-4 mr-1"/>Submit count
          </Button>
        </div>
      )}
    </div>
  );
}

// -------------------- Submitted Counts --------------------
function SubmittedCountsTab({ user, storeIdOverride }) {
  const [counts, setCounts] = useState([]);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    const base = `/inventory/counts?include_deleted=${includeDeleted}`;
    const url = storeIdOverride ? `${base}&store_id=${storeIdOverride}` : base;
    api.get(url).then((r) => setCounts(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [includeDeleted, storeIdOverride]);

  const active = counts.filter((c) => !c.deleted_at);
  const deleted = counts.filter((c) => c.deleted_at);

  const download = async (c) => {
    try {
      const resp = await api.get(`/inventory/counts/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `count-${c.store_name}-${c.date}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submitted count? You can restore within 1 hour.")) return;
    try { await api.delete(`/inventory/counts/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const restore = async (id) => {
    try { await api.post(`/inventory/counts/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} data-testid="include-deleted"/>
          Show recently deleted
        </label>
      </div>

      <div className="space-y-3">
        {active.length === 0 && !includeDeleted && (
          <div className="bg-white rounded-2xl border p-10 text-center text-neutral-500" data-testid="counts-empty">No submitted counts yet.</div>
        )}
        {active.map((c) => (
          <CountRow key={c.id} c={c} user={user} open={openId === c.id} onToggle={() => setOpenId(openId === c.id ? null : c.id)} onDownload={() => download(c)} onDelete={() => remove(c.id)} onSaved={load}/>
        ))}
        {includeDeleted && deleted.length > 0 && (
          <>
            <h3 className="text-sm font-semibold text-neutral-500 mt-6 mb-2 uppercase tracking-wider">Recently deleted</h3>
            {deleted.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-neutral-200/70 p-4 opacity-70" data-testid={`deleted-count-${c.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-medium">{c.store_name} · {c.date}</div>
                    <div className="text-xs text-neutral-500">Deleted · {c.seconds_left > 0 ? `${Math.floor(c.seconds_left/60)} min left to restore` : "restore window expired"}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restore(c.id)} disabled={!c.restorable && !["admin","super_admin"].includes(user?.role)} data-testid={`restore-${c.id}`}><Undo2 className="w-3 h-3 mr-1"/>Restore</Button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function CountRow({ c, user, open, onToggle, onDownload, onDelete, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [items, setItems] = useState(c.items || []);
  useEffect(() => { setItems(c.items || []); }, [c]);

  const approve = async (status) => {
    try { await api.patch(`/inventory/counts/${c.id}/approve`, { status }); toast.success(status === "approved" ? "Approved" : "Rejected"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/inventory/counts/${c.id}`, { items });
      toast.success("Saved");
      setEditing(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-neutral-200/70" data-testid={`count-${c.id}`}>
      <div className="p-4 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-medium flex items-center gap-2">
            {c.store_name} · {c.date}
            {c.is_late && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">LATE</span>}
            {c.approval_status === "pending" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">Pending approval</span>}
            {c.approval_status === "rejected" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Rejected</span>}
            {c.approval_status === "approved" && !c.is_late && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Approved</span>}
          </div>
          <div className="text-xs text-neutral-500">By {c.submitted_by_name} · {(c.submitted_at || "").slice(0, 16).replace("T", " ")}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {["admin","super_admin"].includes(user?.role) && c.approval_status === "pending" && (
            <>
              <Button size="sm" variant="outline" onClick={() => approve("approved")} className="text-emerald-700" data-testid={`approve-count-${c.id}`}><Check className="w-3 h-3 mr-1"/>Approve</Button>
              <Button size="sm" variant="outline" onClick={() => approve("rejected")} className="text-red-600" data-testid={`reject-count-${c.id}`}><X className="w-3 h-3 mr-1"/>Reject</Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={onDownload} data-testid={`download-${c.id}`}><Download className="w-3 h-3 mr-1"/>PDF</Button>
          <Button size="sm" variant="outline" onClick={onToggle}>{open ? "Hide" : "View"}</Button>
          <Button size="sm" variant="outline" onClick={onDelete} className="text-red-600"><Trash2 className="w-3 h-3"/></Button>
        </div>
      </div>
      {open && (
        <div className="border-t border-neutral-100 p-4">
          <div className="flex justify-end mb-2">
            {editing
              ? <div className="flex gap-2"><Button size="sm" onClick={saveEdit} className="bg-[hsl(var(--primary))]" data-testid={`save-edit-${c.id}`}>Save</Button><Button size="sm" variant="outline" onClick={() => { setEditing(false); setItems(c.items || []); }}>Cancel</Button></div>
              : <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`edit-count-${c.id}`}><Pencil className="w-3 h-3 mr-1"/>Edit</Button>}
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-neutral-500">
              <tr><th className="text-left p-2">Item</th><th className="text-left p-2">Unit</th><th className="text-left p-2">Count</th><th className="text-left p-2">Notes</th></tr>
            </thead>
            <tbody>
              {(items || []).map((it, i) => (
                <tr key={it.item_id || `${it.name}-${i}`} className="border-t border-neutral-100">
                  <td className="p-2">{it.name}</td>
                  <td className="p-2 text-neutral-500">{it.unit}</td>
                  <td className="p-2">
                    {editing
                      ? <Input type="number" step="0.01" value={it.count} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, count: parseFloat(e.target.value) || 0 } : x))} className="w-24 h-8"/>
                      : it.count}
                  </td>
                  <td className="p-2">
                    {editing
                      ? <Input value={it.notes || ""} onChange={(e) => setItems(items.map((x, j) => j === i ? { ...x, notes: e.target.value } : x))} className="h-8"/>
                      : <span className="text-neutral-500">{it.notes || "—"}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// -------------------- Late Requests (admin) --------------------
function LateRequestsTab() {
  const [reqs, setReqs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const load = () => {
    const url = statusFilter ? `/inventory/late-requests?status_filter=${statusFilter}` : "/inventory/late-requests";
    api.get(url).then((r) => setReqs(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const STATUS_STYLE = {
    approved: "bg-emerald-100 text-emerald-700",
    rejected: "bg-red-100 text-red-700",
    pending: "bg-yellow-100 text-yellow-800",
  };
  const decide = async (id, status) => {
    try {
      await api.patch(`/inventory/late-requests/${id}`, { status });
      toast.success(status === "approved" ? "Approved" : "Rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="mb-3">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-lg border border-neutral-200 bg-white text-sm px-3" data-testid="late-status-filter">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>
      <div className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="p-4">Employee</th><th className="p-4">Store · Date</th><th className="p-4">Reason</th><th className="p-4">Status</th><th className="p-4"></th></tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {reqs.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-neutral-500" data-testid="late-empty">No late-submission requests.</td></tr>}
            {reqs.map((r) => (
              <tr key={r.id} data-testid={`late-req-${r.id}`}>
                <td className="p-4">{r.employee_name}</td>
                <td className="p-4">{r.date}</td>
                <td className="p-4 text-neutral-600">{r.reason || "—"}</td>
                <td className="p-4">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>{r.status}</span>
                </td>
                <td className="p-4 whitespace-nowrap">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "approved")} className="text-emerald-700 mr-2" data-testid={`late-approve-${r.id}`}><Check className="w-3 h-3 mr-1"/>Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decide(r.id, "rejected")} className="text-red-600" data-testid={`late-reject-${r.id}`}><X className="w-3 h-3 mr-1"/>Reject</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
